import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Database, Plus, Trash2, Wifi, RefreshCw, Loader2, KeyRound,
  FolderOpen, Folder, File as FileIcon, Upload, Download, ChevronRight, HardDrive,
} from 'lucide-react';
import { api, ApiError } from '../api/client.js';

/**
 * /storage — Object Storage (BUILD-QUEUE §2).
 *
 * Phase A: provider accounts manager (AWS S3 + GCS). Paste credentials →
 * test (lists buckets) → save (encrypted server-side). Buckets + object
 * browser arrive in Phase B (an expandable panel per account).
 */
export function Storage() {
  const [accounts, setAccounts] = useState(null);
  const [providers, setProviders] = useState(null);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try {
      const [accData, provData] = await Promise.all([
        api('/api/storage/accounts'),
        api('/api/storage/providers'),
      ]);
      setAccounts(accData.accounts);
      setProviders(provData.providers);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'network_error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-t3">
            Manage buckets & objects on AWS S3 and Google Cloud Storage from the panel.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          disabled={!providers}
          className="inline-flex items-center gap-1.5 bg-blue text-white text-sm px-3 py-1.5 rounded-md hover:bg-blue-deep disabled:opacity-50"
        >
          <Plus className="w-4 h-4" /> Add account
        </button>
      </div>

      {showForm && providers && (
        <AddAccountForm
          providers={providers}
          onCreated={() => { setShowForm(false); load(); }}
        />
      )}

      {error && (
        <div className="bg-err-bg border border-err-bd text-err text-sm rounded-md px-4 py-3">
          {error}
        </div>
      )}

      {accounts === null ? (
        <p className="text-sm text-t3">Loading…</p>
      ) : accounts.length === 0 ? (
        <div className="bg-white border border-bd rounded-lg p-8 text-center text-sm text-t3">
          <Database className="w-8 h-8 mx-auto mb-3 text-ic-light" />
          No storage accounts yet. Add AWS S3 keys or a Google Cloud service-account
          JSON to browse and manage buckets from the panel.
        </div>
      ) : (
        <AccountsTable accounts={accounts} onChanged={load} />
      )}
    </div>
  );
}

const PROVIDER_META = {
  s3: { cls: 'bg-s-3 text-t2 border-bd-strong' },
  gcs: { cls: 'bg-s-blue text-blue border-bd-blue' },
  'linode-os': { cls: 'bg-s-green text-ok border-bd' },
  'do-spaces': { cls: 'bg-s-blue text-blue border-bd-blue' },
};
const PROVIDER_LABEL = {
  s3: 'AWS S3', gcs: 'Google Cloud Storage', 'linode-os': 'Linode OS', 'do-spaces': 'DO Spaces',
};

function ProviderBadge({ provider }) {
  const meta = PROVIDER_META[provider] ?? { cls: 'bg-s-3 text-t2 border-bd' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${meta.cls}`}>
      {PROVIDER_LABEL[provider] ?? provider}
    </span>
  );
}

function StatusBadge({ status }) {
  const styles = {
    ok: 'bg-s-green text-ok border-bd',
    failed: 'bg-err-bg text-err border-err-bd',
    new: 'bg-s-3 text-t2 border-bd',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${styles[status] ?? styles.new}`}>
      {status}
    </span>
  );
}

function AccountsTable({ accounts, onChanged }) {
  return (
    <div className="bg-white border border-bd rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-t3 border-b border-bd">
            <th className="px-4 py-3">Provider</th>
            <th className="px-4 py-3">Label</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Last checked</th>
            <th className="px-4 py-3">Added</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((a) => (
            <AccountRow key={a.id} account={a} onChanged={onChanged} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AccountRow({ account, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [bucketsOpen, setBucketsOpen] = useState(false);

  async function handleTest() {
    setBusy(true);
    setTestResult(null);
    try {
      const r = await api(`/api/storage/accounts/${account.id}/test`, { method: 'POST' });
      setTestResult({ ok: true, detail: r.detail });
    } catch (err) {
      setTestResult({ ok: false, detail: err.body?.detail ?? err.message });
    } finally {
      setBusy(false);
      onChanged();
    }
  }

  async function handleDelete() {
    const typed = window.prompt(
      `Type the account label to confirm removal:\n${account.label}\n\n(Removes the encrypted credentials from the panel — nothing is deleted at the provider.)`,
    );
    if (typed === null) return;
    if (typed !== account.label) {
      window.alert('Label did not match — not removed.');
      return;
    }
    setBusy(true);
    try {
      await api(`/api/storage/accounts/${account.id}`, {
        method: 'DELETE',
        body: { confirm_label: typed },
      });
      onChanged();
    } catch (err) {
      window.alert(`Delete failed: ${err.message}`);
      setBusy(false);
    }
  }

  return (
    <>
    <tr className="border-b border-bd last:border-0">
      <td className="px-4 py-3"><ProviderBadge provider={account.provider} /></td>
      <td className="px-4 py-3 font-medium text-t1">{account.label}</td>
      <td className="px-4 py-3">
        <StatusBadge status={account.status} />
        {testResult && (
          <span className={`ml-2 text-xs ${testResult.ok ? 'text-ok' : 'text-err'}`}>
            {testResult.detail}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-t3 text-xs">
        {account.last_checked_at ? new Date(`${account.last_checked_at}Z`).toLocaleString() : '—'}
      </td>
      <td className="px-4 py-3 text-t3 text-xs">
        {new Date(`${account.created_at}Z`).toLocaleDateString()}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1.5">
          <button
            type="button"
            onClick={() => setBucketsOpen((v) => !v)}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-bd-blue text-blue hover:bg-s-blue"
          >
            <HardDrive className="w-3.5 h-3.5" /> Buckets
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={handleTest}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-bd text-t2 hover:bg-s-2 disabled:opacity-50"
          >
            {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Wifi className="w-3.5 h-3.5" />}
            Test
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={handleDelete}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-err-bd text-err hover:bg-err-bg disabled:opacity-50"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      </td>
    </tr>
    {bucketsOpen && (
      <tr className="border-b border-bd last:border-0 bg-s-blue/30">
        <td colSpan={6} className="px-4 py-4">
          <BucketsPanel account={account} />
        </td>
      </tr>
    )}
    </>
  );
}

// ─── Phase B: buckets + object browser ──────────────────────────────────────

function formatBytes(n) {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

function BucketsPanel({ account }) {
  const [buckets, setBuckets] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [openBucket, setOpenBucket] = useState(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const data = await api(`/api/storage/accounts/${account.id}/buckets`);
      setBuckets(data.buckets);
      setError(null);
    } catch (err) {
      setError(err.body?.detail ?? err.message);
    } finally {
      setRefreshing(false);
    }
  }, [account.id]);

  useEffect(() => { load(); }, [load]);

  async function handleDeleteBucket(b) {
    // Probe emptiness first so the confirmation can be honest about cascade.
    let stats = null;
    try {
      stats = await api(`/api/storage/accounts/${account.id}/buckets/${encodeURIComponent(b.name)}/stats`);
    } catch { /* fall through — the server re-checks anyway */ }

    const nonEmpty = stats && !stats.empty;
    const warn = nonEmpty
      ? `\n\n⚠ This bucket is NOT empty (${stats.sample_count}${stats.more ? '+' : ''} object(s)).\nDeleting it will PERMANENTLY DELETE ALL its contents first.`
      : '';
    const typed = window.prompt(
      `DELETE bucket "${b.name}"?${warn}\n\nThis cannot be undone. Type the bucket name to confirm:`,
    );
    if (typed === null) return;
    if (typed !== b.name) { window.alert('Name did not match — not deleted.'); return; }
    if (nonEmpty && !window.confirm(`Last check — delete "${b.name}" AND all its objects?`)) return;

    try {
      await api(`/api/storage/accounts/${account.id}/buckets/${encodeURIComponent(b.name)}`, {
        method: 'DELETE',
        body: { confirm_name: typed, force: Boolean(nonEmpty) },
      });
      if (openBucket === b.name) setOpenBucket(null);
      load();
    } catch (err) {
      window.alert(`Delete failed: ${err.body?.detail ?? err.message}`);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-t1">
          <HardDrive className="w-4 h-4 text-t3" /> Buckets on {account.label}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={load}
            disabled={refreshing}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-bd text-t2 hover:bg-s-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button
            type="button"
            onClick={() => setShowCreate((v) => !v)}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-blue text-white hover:bg-blue-deep"
          >
            <Plus className="w-3.5 h-3.5" /> Create bucket
          </button>
        </div>
      </div>

      {showCreate && (
        <CreateBucketForm
          account={account}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      {error && (
        <div className="bg-err-bg border border-err-bd text-err text-sm rounded-md px-3 py-2">{error}</div>
      )}

      {buckets === null ? (
        <p className="text-sm text-t3">Loading buckets…</p>
      ) : buckets.length === 0 ? (
        <p className="text-sm text-t3 bg-white border border-bd rounded-md px-4 py-4 text-center">
          No buckets on this account yet.
        </p>
      ) : (
        <div className="bg-white border border-bd rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-t3 border-b border-bd">
                <th className="px-3 py-2">Bucket</th>
                <th className="px-3 py-2">Region</th>
                <th className="px-3 py-2">Created</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {buckets.map((b) => (
                <tr key={b.name} className="border-b border-bd last:border-0">
                  <td className="px-3 py-2 font-medium text-t1">{b.name}</td>
                  <td className="px-3 py-2 text-t2 text-xs font-mono">{b.region ?? '—'}</td>
                  <td className="px-3 py-2 text-t3 text-xs">
                    {b.created_at ? new Date(b.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        type="button"
                        onClick={() => setOpenBucket(openBucket === b.name ? null : b.name)}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-bd-blue text-blue hover:bg-s-blue"
                      >
                        <FolderOpen className="w-3.5 h-3.5" />
                        {openBucket === b.name ? 'Close' : 'Browse'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteBucket(b)}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-err-bd text-err hover:bg-err-bg"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openBucket && (
        <ObjectBrowser account={account} bucket={openBucket} />
      )}
    </div>
  );
}

function CreateBucketForm({ account, onCreated }) {
  const [regions, setRegions] = useState(null);
  const [name, setName] = useState('');
  const [region, setRegion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await api(`/api/storage/accounts/${account.id}/regions`);
        if (alive) { setRegions(d.regions); setRegion(d.regions[0] ?? ''); }
      } catch (err) {
        if (alive) setError(err.body?.detail ?? err.message);
      }
    })();
    return () => { alive = false; };
  }, [account.id]);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true); setError(null);
    try {
      await api(`/api/storage/accounts/${account.id}/buckets`, {
        method: 'POST',
        body: { name: name.trim(), region },
      });
      onCreated();
    } catch (err) {
      setError(err.body?.detail?.join?.(', ') ?? err.body?.detail ?? err.message);
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-bd rounded-md p-4 space-y-3">
      <div className="grid sm:grid-cols-3 gap-3">
        <label className="block sm:col-span-2">
          <span className="text-xs font-medium text-t2">Bucket name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="my-bucket-name"
            className="mt-1 w-full rounded-md border border-bd-strong px-2.5 py-1.5 text-sm font-mono"
          />
          <span className="text-[11px] text-t3">
            3-63 chars, lowercase letters/digits/hyphen/dot, globally unique.
          </span>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-t2">Region</span>
          <select
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className="mt-1 w-full rounded-md border border-bd-strong px-2.5 py-1.5 text-sm bg-white"
          >
            {(regions ?? []).map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
      </div>

      {error && (
        <div className="bg-err-bg border border-err-bd text-err text-sm rounded-md px-3 py-2">{error}</div>
      )}

      <button
        type="submit"
        disabled={submitting || !name.trim() || !regions}
        className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-blue text-white hover:bg-blue-deep disabled:opacity-50"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        Create bucket
      </button>
    </form>
  );
}

function ObjectBrowser({ account, bucket }) {
  const [prefix, setPrefix] = useState('');
  const [page, setPage] = useState(null);      // { objects, folders, next_token, truncated }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [uploadPct, setUploadPct] = useState(null);
  const fileInput = useRef(null);

  const base = `/api/storage/accounts/${account.id}/buckets/${encodeURIComponent(bucket)}`;

  const load = useCallback(async (pfx, append = false, token = null) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ prefix: pfx, limit: '100' });
      if (token) qs.set('token', token);
      const d = await api(`${base}/objects?${qs}`);
      setPage((prev) => (append && prev ? {
        ...d,
        objects: [...prev.objects, ...d.objects],
        folders: [...prev.folders, ...d.folders],
      } : d));
      setError(null);
    } catch (err) {
      setError(err.body?.detail ?? err.message);
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => { setSelected(new Set()); load(prefix); }, [prefix, load]);

  function navigate(newPrefix) { setPrefix(newPrefix); }

  // Breadcrumb segments from the current prefix.
  const segments = prefix ? prefix.replace(/\/$/, '').split('/') : [];

  function toggle(key) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  async function handleDeleteSelected() {
    const keys = [...selected];
    if (!keys.length) return;
    const typed = window.prompt(
      `DELETE ${keys.length} object(s) from "${bucket}"?\n\n${keys.slice(0, 10).map((k) => `• ${k}`).join('\n')}${keys.length > 10 ? `\n…and ${keys.length - 10} more` : ''}\n\nThis cannot be undone. Type the number of objects (${keys.length}) to confirm:`,
    );
    if (typed === null) return;
    if (Number(typed) !== keys.length) { window.alert('Count did not match — nothing deleted.'); return; }
    try {
      const r = await api(`${base}/objects/delete`, {
        method: 'POST',
        body: { keys, confirm_count: keys.length },
      });
      if (r.errors?.length) {
        window.alert(`Some deletes failed:\n${r.errors.map((e) => `${e.key}: ${e.message}`).join('\n')}`);
      }
      setSelected(new Set());
      load(prefix);
    } catch (err) {
      window.alert(`Delete failed: ${err.body?.detail ?? err.message}`);
    }
  }

  // XHR upload so we get a real progress percentage (fetch can't report it).
  function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append('file', file, file.name);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${base}/objects?prefix=${encodeURIComponent(prefix)}`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) setUploadPct(Math.round((ev.loaded / ev.total) * 100));
    };
    xhr.onload = () => {
      setUploadPct(null);
      if (fileInput.current) fileInput.current.value = '';
      if (xhr.status >= 200 && xhr.status < 300) {
        load(prefix);
      } else {
        let msg = `HTTP ${xhr.status}`;
        try { const b = JSON.parse(xhr.responseText); msg = b.detail ?? b.error ?? msg; } catch { /* keep */ }
        window.alert(`Upload failed: ${msg}`);
      }
    };
    xhr.onerror = () => { setUploadPct(null); window.alert('Upload failed: network error'); };
    xhr.send(form);
  }

  return (
    <div className="bg-white border border-bd rounded-md p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1 text-sm min-w-0 flex-wrap">
          <button
            type="button"
            onClick={() => navigate('')}
            className="font-medium text-t1 hover:text-blue"
          >
            {bucket}
          </button>
          {segments.map((seg, i) => {
            const upto = `${segments.slice(0, i + 1).join('/')}/`;
            return (
              <span key={upto} className="flex items-center gap-1 min-w-0">
                <ChevronRight className="w-3.5 h-3.5 text-t3 shrink-0" />
                <button
                  type="button"
                  onClick={() => navigate(upto)}
                  className="text-t2 hover:text-blue truncate"
                >
                  {seg}
                </button>
              </span>
            );
          })}
        </div>

        <div className="flex items-center gap-1.5">
          {selected.size > 0 && (
            <button
              type="button"
              onClick={handleDeleteSelected}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-err-bd bg-err-bg text-err hover:bg-err-bg"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete selected ({selected.size})
            </button>
          )}
          <button
            type="button"
            onClick={() => load(prefix)}
            disabled={loading}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-bd text-t2 hover:bg-s-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            disabled={uploadPct !== null}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-blue text-white hover:bg-blue-deep disabled:opacity-50"
          >
            <Upload className="w-3.5 h-3.5" />
            {uploadPct !== null ? `Uploading ${uploadPct}%` : 'Upload'}
          </button>
          <input ref={fileInput} type="file" onChange={handleUpload} className="hidden" />
        </div>
      </div>

      {uploadPct !== null && (
        <div className="h-1.5 bg-s-3 rounded-full overflow-hidden">
          <div className="h-full bg-blue transition-all" style={{ width: `${uploadPct}%` }} />
        </div>
      )}

      {error && (
        <div className="bg-err-bg border border-err-bd text-err text-sm rounded-md px-3 py-2">{error}</div>
      )}

      {page === null ? (
        <p className="text-sm text-t3">Loading…</p>
      ) : page.folders.length === 0 && page.objects.length === 0 ? (
        <p className="text-sm text-t3 text-center py-6">
          This {prefix ? 'folder' : 'bucket'} is empty. Use Upload to add a file.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-t3 border-b border-bd">
                <th className="px-2 py-2 w-8"></th>
                <th className="px-2 py-2">Name</th>
                <th className="px-2 py-2">Size</th>
                <th className="px-2 py-2">Modified</th>
                <th className="px-2 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {page.folders.map((f) => (
                <tr key={f.prefix} className="border-b border-bd last:border-0">
                  <td className="px-2 py-2"></td>
                  <td className="px-2 py-2" colSpan={3}>
                    <button
                      type="button"
                      onClick={() => navigate(f.prefix)}
                      className="inline-flex items-center gap-1.5 text-t1 hover:text-blue"
                    >
                      <Folder className="w-4 h-4 text-warn" />
                      {f.name}/
                    </button>
                  </td>
                  <td className="px-2 py-2"></td>
                </tr>
              ))}
              {page.objects.map((o) => (
                <tr key={o.key} className="border-b border-bd last:border-0">
                  <td className="px-2 py-2">
                    <input type="checkbox" checked={selected.has(o.key)} onChange={() => toggle(o.key)} />
                  </td>
                  <td className="px-2 py-2">
                    <span className="inline-flex items-center gap-1.5 text-t1">
                      <FileIcon className="w-4 h-4 text-t3" />
                      <span className="truncate max-w-md" title={o.key}>{o.name || o.key}</span>
                    </span>
                  </td>
                  <td className="px-2 py-2 text-t2 text-xs">{formatBytes(o.size)}</td>
                  <td className="px-2 py-2 text-t3 text-xs">
                    {o.last_modified ? new Date(o.last_modified).toLocaleString() : '—'}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-center justify-end">
                      <a
                        href={`${base}/download?key=${encodeURIComponent(o.key)}`}
                        download
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-bd text-t2 hover:bg-s-2"
                      >
                        <Download className="w-3.5 h-3.5" /> Download
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {page?.truncated && page.next_token && (
        <button
          type="button"
          onClick={() => load(prefix, true, page.next_token)}
          disabled={loading}
          className="text-xs px-3 py-1.5 rounded-md border border-bd-strong text-t2 hover:bg-s-2 disabled:opacity-50"
        >
          {loading ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  );
}

// ─── Add account form (data-driven from /api/storage/providers) ─────────────

function AddAccountForm({ providers, onCreated }) {
  const [provider, setProvider] = useState(providers[0].id);
  const [label, setLabel] = useState('');
  const [fields, setFields] = useState({});
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);

  const meta = providers.find((p) => p.id === provider) ?? providers[0];

  function reset() { setTestResult(null); setError(null); }
  function setField(name, value) { setFields((f) => ({ ...f, [name]: value })); reset(); }

  const credentials = Object.fromEntries(
    (meta.fields ?? []).map((f) => [f.name, (fields[f.name] ?? '').trim()]),
  );
  const credsFilled = (meta.fields ?? [])
    .filter((f) => f.required !== false)
    .every((f) => (fields[f.name] ?? '').trim().length > 0);

  const canTest = credsFilled && !testing && !saving;
  const canSave = testResult?.ok && label.trim().length >= 2 && !testing && !saving;

  async function handleTest() {
    setTesting(true); reset();
    try {
      const r = await api('/api/storage/accounts/test', {
        method: 'POST',
        body: { provider, credentials },
      });
      setTestResult({ ok: true, detail: r.detail });
    } catch (err) {
      setTestResult({ ok: false, detail: err.body?.detail ?? err.message });
    } finally {
      setTesting(false);
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      await api('/api/storage/accounts', {
        method: 'POST',
        body: { provider, label: label.trim(), credentials },
      });
      onCreated();
    } catch (err) {
      setError(err.body?.detail?.join?.(', ') ?? err.body?.detail ?? err.message);
      setSaving(false);
    }
  }

  const inputCls = 'mt-1 w-full rounded-md border border-bd-strong px-2.5 py-1.5 text-sm';

  return (
    <form onSubmit={handleSave} className="bg-white border border-bd rounded-lg p-5 space-y-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-t1">
        <KeyRound className="w-4 h-4 text-t3" /> Add storage account
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs font-medium text-t2">Provider</span>
          <select
            value={provider}
            onChange={(e) => { setProvider(e.target.value); setFields({}); reset(); }}
            className={`${inputCls} bg-white`}
          >
            {providers.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-t2">Label</span>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={`${meta.label} main`}
            className={inputCls}
          />
        </label>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {(meta.fields ?? []).map((f) => (
          <label key={f.name} className={f.multiline ? 'block sm:col-span-2' : 'block'}>
            <span className="text-xs font-medium text-t2">
              {f.label}{f.required === false ? ' (optional)' : ''}
            </span>
            {f.multiline ? (
              <textarea
                rows={6}
                value={fields[f.name] ?? ''}
                onChange={(e) => setField(f.name, e.target.value)}
                placeholder={f.placeholder}
                autoComplete="off"
                className={`${inputCls} font-mono text-xs`}
              />
            ) : (
              <input
                type={f.secret ? 'password' : 'text'}
                value={fields[f.name] ?? ''}
                onChange={(e) => setField(f.name, e.target.value)}
                placeholder={f.placeholder}
                autoComplete="off"
                className={`${inputCls} font-mono`}
              />
            )}
          </label>
        ))}
      </div>

      {testResult && (
        <div className={`text-sm rounded-md px-3 py-2 border ${
          testResult.ok
            ? 'bg-s-green border-bd text-ok'
            : 'bg-err-bg border-err-bd text-err'
        }`}>
          {testResult.ok ? <>Connection OK — {testResult.detail}</> : <>Test failed: {testResult.detail}</>}
        </div>
      )}
      {error && (
        <div className="text-sm rounded-md px-3 py-2 border bg-err-bg border-err-bd text-err">
          {error}
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={!canTest}
          onClick={handleTest}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-bd-strong text-t2 hover:bg-s-2 disabled:opacity-50"
        >
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wifi className="w-4 h-4" />}
          Test connection
        </button>
        <button
          type="submit"
          disabled={!canSave}
          title={canSave ? undefined : 'Run a successful connection test first'}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-blue text-white hover:bg-blue-deep disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Save account
        </button>
        <span className="text-xs text-t3 ml-1">
          Credentials are encrypted with the panel master key and never shown again.
        </span>
      </div>
    </form>
  );
}
